import { useMemo } from 'react';
import { Box, ButtonBase, Divider, Tooltip, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import type { UserComplianceSummaryDto } from '../../services/complianceReportService';
import {
  getDeadlineHorizon,
  getEthicsCoverage,
  getStandingCounts,
  getStateMix,
  type DeadlineMonth,
  type EthicsCoverageRollup,
  type LicenseStandingSplit,
  type MemberBucket,
  type StateMixRow,
} from './firmRollup';

/** Status filter shared with the Team Reporting table ('all' + exclusive buckets). */
export type StatusFilter = 'all' | MemberBucket;

interface FirmOverviewProps {
  users: UserComplianceSummaryDto[];
  statusFilter: StatusFilter;
  onStatusFilterChange: (filter: StatusFilter) => void;
  /** Active state filter ('all' or a state code), shared with the table. */
  stateFilter: string;
  onStateFilterChange: (filter: string) => void;
  /** Active deadline-month filter (UTC "YYYY-MM" key or null), shared with the table. */
  deadlineMonthFilter: string | null;
  onDeadlineMonthFilterChange: (filter: string | null) => void;
  /** When true the table shows only members with an ethics shortfall. */
  ethicsFilter: boolean;
  onEthicsFilterChange: (filter: boolean) => void;
  /** Report clock shared with the table so band and roster counts agree. */
  now?: Date;
}

/** One shared color vocabulary for every visual in the band. In-progress is
 *  blue (`status.onPace`), not brand green, so it can never be mistaken for
 *  the success green that means "compliant". */
const BUCKETS: {
  key: MemberBucket;
  label: string;
  token: 'attention' | 'atRisk' | 'onPace' | 'completed' | 'enrolled';
}[] = [
  { key: 'attention', label: 'Attention required', token: 'attention' },
  { key: 'at_risk', label: 'At risk', token: 'atRisk' },
  { key: 'in_progress', label: 'In progress', token: 'onPace' },
  { key: 'concluded', label: 'Compliant', token: 'completed' },
  { key: 'not_set_up', label: 'Not set up', token: 'enrolled' },
];

const COUNT_KEYS: Record<MemberBucket, 'attention' | 'atRisk' | 'inProgress' | 'concluded' | 'notSetUp'> = {
  attention: 'attention',
  at_risk: 'atRisk',
  in_progress: 'inProgress',
  concluded: 'concluded',
  not_set_up: 'notSetUp',
};

/** License-level split segments (horizon + jurisdiction bars). */
const SPLIT_SEGMENTS: {
  key: keyof Omit<LicenseStandingSplit, 'total'>;
  label: string;
  token: 'attention' | 'atRisk' | 'onPace' | 'completed';
}[] = [
  { key: 'attention', label: 'attention required', token: 'attention' },
  { key: 'atRisk', label: 'at risk', token: 'atRisk' },
  { key: 'onPace', label: 'on pace', token: 'onPace' },
  { key: 'concluded', label: 'compliant', token: 'completed' },
];

function splitSummary(split: LicenseStandingSplit): string {
  const parts = SPLIT_SEGMENTS.filter((s) => split[s.key] > 0).map(
    (s) => `${split[s.key]} ${s.label}`
  );
  const noun = split.total === 1 ? 'license' : 'licenses';
  return `${split.total} ${noun}${parts.length > 0 ? `: ${parts.join(', ')}` : ''}`;
}

/**
 * Firm-wide rollup band for Team Reporting: compliance standing (interactive,
 * drives the member table's status filter), reporting-deadline horizon,
 * jurisdiction mix, and ethics coverage. Standing re-scopes to the active
 * jurisdiction filter; the other panels always reflect the whole firm.
 *
 * Renders unwrapped (padded Box, no border): the page mounts it as the header
 * region of the report container it filters, so band and roster share one
 * border and read as a single document.
 */
export default function FirmOverview({
  users,
  statusFilter,
  onStatusFilterChange,
  stateFilter,
  onStateFilterChange,
  deadlineMonthFilter,
  onDeadlineMonthFilterChange,
  ethicsFilter,
  onEthicsFilterChange,
  now: nowProp,
}: FirmOverviewProps) {
  // One clock per report load keeps every visual's "now" consistent; callers
  // can pass their own so band counts match sibling views exactly.
  const fallbackNow = useMemo(() => new Date(), []);
  const now = nowProp ?? fallbackNow;
  const stateScope = stateFilter === 'all' ? undefined : stateFilter;
  // Standing re-scopes to the selected jurisdiction so segment counts keep
  // matching the state-filtered table rows (honest intersection).
  const counts = useMemo(
    () => getStandingCounts(users, now, stateScope),
    [users, now, stateScope]
  );
  const horizon = useMemo(() => getDeadlineHorizon(users, now), [users, now]);
  const stateMix = useMemo(() => getStateMix(users, now), [users, now]);
  const ethics = useMemo(() => getEthicsCoverage(users, now), [users, now]);

  if (users.length === 0) return null;

  const showStateMix = stateMix.length > 1;
  // All set-up licenses, so the horizon aside can reconcile against deadlines
  // that fall beyond its 12-month window.
  const setUpLicenseTotal = stateMix.reduce((sum, row) => sum + row.total, 0);
  const filtering = statusFilter !== 'all';
  const allCompliant = counts.total > 0 && counts.concluded === counts.total;

  const toggleBucket = (bucket: MemberBucket) =>
    onStatusFilterChange(statusFilter === bucket ? 'all' : bucket);

  return (
    <Box sx={{ p: 2.5 }}>
        {/* ── Compliance standing (interactive) ─────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
            Compliance standing
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
            {stateScope
              ? `${counts.total} member${counts.total !== 1 ? 's' : ''} with ${stateScope} licenses`
              : `${counts.total} team member${counts.total !== 1 ? 's' : ''}`}
          </Typography>
        </Box>

        {/* Unit strip: one unit per member so counts stay literally countable
            at firm scale. Mouse affordance only (aria-hidden, no tab stops);
            the legend below is the single accessible filter surface for the
            same actions. */}
        <Box aria-hidden sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: 1, mb: 1.25 }}>
          {BUCKETS.filter((b) => counts[COUNT_KEYS[b.key]] > 0).map((bucket) => {
            const count = counts[COUNT_KEYS[bucket.key]];
            const active = statusFilter === bucket.key;
            return (
              <Tooltip key={bucket.key} title={`${bucket.label}: ${count}`} arrow>
                <ButtonBase
                  tabIndex={-1}
                  onClick={() => toggleBucket(bucket.key)}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '3px',
                    borderRadius: '4px',
                    opacity: filtering && !active ? 0.3 : 1,
                    transition: 'opacity 0.15s ease-out',
                    '&:hover': { opacity: filtering && !active ? 0.55 : 0.85 },
                  }}
                >
                  {Array.from({ length: count }, (_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 9,
                        height: 14,
                        borderRadius: '3px',
                        // Not-set-up members render hollow: present on the
                        // roster, not yet part of the tracked record.
                        ...(bucket.key === 'not_set_up'
                          ? { border: '1.5px solid', borderColor: 'status.enrolled.dot' }
                          : { bgcolor: `status.${bucket.token}.dot` }),
                      }}
                    />
                  ))}
                </ButtonBase>
              </Tooltip>
            );
          })}
        </Box>

        <Box
          role="group"
          aria-label="Filter members by compliance standing"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
        >
          {BUCKETS.map((bucket) => {
            const count = counts[COUNT_KEYS[bucket.key]];
            const active = statusFilter === bucket.key;
            return (
              <ButtonBase
                key={bucket.key}
                onClick={() => toggleBucket(bucket.key)}
                aria-pressed={active}
                aria-label={`${bucket.label}: ${count} member${count !== 1 ? 's' : ''}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: '6px',
                  bgcolor: active ? `status.${bucket.token}.bg` : 'transparent',
                  transition: 'background-color 0.15s ease-out',
                  '&:hover': { bgcolor: active ? `status.${bucket.token}.bg` : 'tint.neutralSoft' },
                  '&.Mui-focusVisible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '1px',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: `status.${bucket.token}.dot`,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 500,
                    color: active ? `status.${bucket.token}.fg` : 'text.secondary',
                  }}
                >
                  {bucket.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontVariantNumeric: 'tabular-nums',
                    color: active ? `status.${bucket.token}.fg` : 'text.primary',
                  }}
                >
                  {count}
                </Typography>
              </ButtonBase>
            );
          })}
        </Box>

        {allCompliant && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'status.completed.fg', fontWeight: 600, mt: 0.75 }}
          >
            All {counts.total} member{counts.total !== 1 ? 's' : ''} compliant. Audit-ready.
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', mt: 0.75 }}
        >
          <FilterListIcon sx={{ fontSize: 14 }} />
          <span>
            Select a status{showStateMix ? ', jurisdiction' : ''}, deadline month, or ethics gap to
            filter the member list.
            {stateScope ? ` Standing reflects ${stateScope} licenses only.` : ''}
          </span>
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* ── Detail panels ─────────────────────────────────────────────── */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: showStateMix ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)' },
            gap: { xs: 3, md: 4 },
          }}
        >
          <DeadlineHorizonPanel
            horizon={horizon}
            totalLicenses={setUpLicenseTotal}
            monthFilter={deadlineMonthFilter}
            onMonthFilterChange={onDeadlineMonthFilterChange}
          />
          {showStateMix && (
            <StateMixPanel
              stateMix={stateMix}
              stateFilter={stateFilter}
              onStateFilterChange={onStateFilterChange}
            />
          )}
          <EthicsCoveragePanel
            ethics={ethics}
            active={ethicsFilter}
            onToggle={onEthicsFilterChange}
            stateFilter={stateFilter}
            onStateFilterChange={onStateFilterChange}
          />
        </Box>
    </Box>
  );
}

// ── Panels ────────────────────────────────────────────────────────────────────

function PanelTitle({ children, aside }: { children: string; aside?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
      <Typography variant="overline" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
        {children}
      </Typography>
      {aside && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {aside}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Persistent "this row filters the table" affordance. Faint at rest so the
 * rows read as interactive without shouting; darkens when its row is hovered.
 */
function RowChevron() {
  return (
    <ChevronRightIcon
      sx={{
        fontSize: 16,
        color: 'text.disabled',
        alignSelf: 'center',
        justifySelf: 'end',
        transition: 'color 0.15s ease-out',
        '.MuiButtonBase-root:hover &': { color: 'text.secondary' },
      }}
    />
  );
}

function PanelEmpty({ children }: { children: string }) {
  return (
    <Typography variant="body2" sx={{ color: 'text.disabled', py: 3, textAlign: 'center' }}>
      {children}
    </Typography>
  );
}

/**
 * Agenda of reporting-period ends over the next 12 months. Deadline data is
 * spiky (most boards share year-end/mid-year period ends), so only months that
 * actually have deadlines are listed, worded and worst-counts-first. Rows
 * toggle the table's deadline-month filter, matching the rest of the band's
 * click-to-filter contract.
 */
function DeadlineHorizonPanel({
  horizon,
  totalLicenses,
  monthFilter,
  onMonthFilterChange,
}: {
  horizon: DeadlineMonth[];
  totalLicenses: number;
  monthFilter: string | null;
  onMonthFilterChange: (filter: string | null) => void;
}) {
  const monthsWithDeadlines = horizon.filter((m) => m.total > 0);
  const withinWindow = monthsWithDeadlines.reduce((sum, m) => sum + m.total, 0);
  const filtering = monthFilter !== null;
  return (
    <Box>
      <PanelTitle
        aside={
          withinWindow === 0
            ? undefined
            : withinWindow < totalLicenses
              ? `${withinWindow} of ${totalLicenses} licenses`
              : `${withinWindow} license${withinWindow !== 1 ? 's' : ''}`
        }
      >
        Reporting deadlines
      </PanelTitle>
      {monthsWithDeadlines.length === 0 ? (
        <PanelEmpty>No reporting deadlines in the next 12 months.</PanelEmpty>
      ) : (
        <Box
          role="group"
          aria-label="Filter members by reporting-deadline month"
          sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
        >
          {monthsWithDeadlines.map((month) => {
            const active = monthFilter === month.key;
            const label = `${month.label} ${month.yearLabel}: ${splitSummary(month)}`;
            return (
              <Tooltip key={month.key} title={label} arrow>
                <ButtonBase
                  onClick={() => onMonthFilterChange(active ? null : month.key)}
                  aria-pressed={active}
                  aria-label={label}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '52px 1fr 16px',
                    alignItems: 'baseline',
                    gap: 1,
                    textAlign: 'left',
                    px: 0.75,
                    py: 0.5,
                    mx: -0.75,
                    borderRadius: '6px',
                    bgcolor: active ? 'tint.neutralSoft' : 'transparent',
                    opacity: filtering && !active ? 0.45 : 1,
                    transition: 'background-color 0.15s ease-out, opacity 0.15s ease-out',
                    '&:hover': { bgcolor: 'tint.neutralSoft', opacity: 1 },
                    '&.Mui-focusVisible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '1px',
                    },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
                  >
                    {month.label} {month.yearLabel}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.primary' }}>
                    {month.total} license{month.total !== 1 ? 's' : ''} due
                    {month.atRisk > 0 && (
                      <Box component="span" sx={{ color: 'status.atRisk.fg', fontWeight: 600 }}>
                        {' · '}
                        {month.atRisk} at risk
                      </Box>
                    )}
                    {month.attention > 0 && (
                      <Box component="span" sx={{ color: 'status.attention.fg', fontWeight: 600 }}>
                        {' · '}
                        {month.attention} need{month.attention === 1 ? 's' : ''} attention
                      </Box>
                    )}
                  </Typography>
                  <RowChevron />
                </ButtonBase>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

/** One comparable-scale stacked bar per jurisdiction; rows toggle the table's state filter. */
function StateMixPanel({
  stateMix,
  stateFilter,
  onStateFilterChange,
}: {
  stateMix: StateMixRow[];
  stateFilter: string;
  onStateFilterChange: (filter: string) => void;
}) {
  const max = Math.max(...stateMix.map((r) => r.total));
  const licenseTotal = stateMix.reduce((sum, r) => sum + r.total, 0);
  const filtering = stateFilter !== 'all';
  return (
    <Box>
      <PanelTitle aside={`${licenseTotal} license${licenseTotal !== 1 ? 's' : ''}`}>
        By jurisdiction
      </PanelTitle>
      <Box
        role="group"
        aria-label="Filter members by jurisdiction"
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
      >
        {stateMix.map((row) => {
          const active = stateFilter === row.stateCode;
          return (
            <Tooltip key={row.stateCode} title={`${row.stateCode}: ${splitSummary(row)}`} arrow>
              <ButtonBase
                onClick={() => onStateFilterChange(active ? 'all' : row.stateCode)}
                aria-pressed={active}
                aria-label={`${row.stateCode}: ${splitSummary(row)}`}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '32px 1fr 28px 16px',
                  alignItems: 'center',
                  gap: 1,
                  textAlign: 'left',
                  // Padding + negative margin: hover/active bg gets breathing
                  // room while content stays aligned with the panel title.
                  px: 0.75,
                  py: 0.5,
                  mx: -0.75,
                  borderRadius: '6px',
                  bgcolor: active ? 'tint.neutralSoft' : 'transparent',
                  opacity: filtering && !active ? 0.45 : 1,
                  transition: 'background-color 0.15s ease-out, opacity 0.15s ease-out',
                  '&:hover': { bgcolor: 'tint.neutralSoft', opacity: 1 },
                  '&.Mui-focusVisible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '1px',
                  },
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {row.stateCode}
                </Typography>
                <Box sx={{ display: 'flex', gap: '2px', height: 8 }}>
                  {SPLIT_SEGMENTS.filter((s) => row[s.key] > 0).map((segment, i, visible) => (
                    <Box
                      key={segment.key}
                      sx={{
                        width: `${(row[segment.key] / max) * 100}%`,
                        minWidth: 4,
                        bgcolor: `status.${segment.token}.dot`,
                        borderRadius:
                          visible.length === 1
                            ? '999px'
                            : i === 0
                              ? '999px 0 0 999px'
                              : i === visible.length - 1
                                ? '0 999px 999px 0'
                                : 0,
                      }}
                    />
                  ))}
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', textAlign: 'right' }}
                >
                  {row.total}
                </Typography>
                <RowChevron />
              </ButtonBase>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * Firm-wide ethics exposure. The headline stays the shortfall alert ("6 of 10
 * licenses short on ethics hours") because that's what the click filters to,
 * but each jurisdiction row reads as progress: the green fill is the share of
 * tracked ethics targets already met, so the bar fills up as requirements are
 * satisfied. The headline toggles the table's firm-wide ethics-gap filter; a
 * state row composes the ethics filter with that jurisdiction's state filter.
 */
function EthicsCoveragePanel({
  ethics,
  active,
  onToggle,
  stateFilter,
  onStateFilterChange,
}: {
  ethics: EthicsCoverageRollup;
  active: boolean;
  onToggle: (filter: boolean) => void;
  stateFilter: string;
  onStateFilterChange: (filter: string) => void;
}) {
  const { rows, shortTotal, trackedTotal } = ethics;
  return (
    <Box>
      {/* The shortfall headline already carries the denominator ("6 of 10"),
          so the tracked-count aside only appears when everything is met. */}
      <PanelTitle
        aside={
          trackedTotal > 0 && shortTotal === 0
            ? `${trackedTotal} tracked license${trackedTotal !== 1 ? 's' : ''}`
            : undefined
        }
      >
        Ethics coverage
      </PanelTitle>
      {trackedTotal === 0 ? (
        <PanelEmpty>No tracked ethics requirements.</PanelEmpty>
      ) : shortTotal === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 1 }}>
          <Box
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'status.completed.dot', flexShrink: 0 }}
          />
          <Typography variant="caption" sx={{ color: 'status.completed.fg', fontWeight: 600 }}>
            All tracked ethics requirements met
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <ButtonBase
            onClick={() => onToggle(!active)}
            aria-pressed={active}
            aria-label={`${shortTotal} of ${trackedTotal} licenses short on ethics hours: filter the member list`}
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              gap: 0.75,
              px: 0.75,
              py: 0.5,
              mx: -0.75,
              borderRadius: '6px',
              textAlign: 'left',
              bgcolor: active ? 'status.atRisk.bg' : 'transparent',
              transition: 'background-color 0.15s ease-out',
              '&:hover': { bgcolor: active ? 'status.atRisk.bg' : 'tint.neutralSoft' },
              '&.Mui-focusVisible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: '1px',
              },
            }}
          >
            {/* Top-aligned with a nudge so the dot stays on the first line when
                the headline wraps at narrow widths. */}
            <Box
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'status.atRisk.dot', flexShrink: 0, mt: '5px' }}
            />
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'status.atRisk.fg', flexGrow: 1 }}>
              {shortTotal} of {trackedTotal} licenses short on ethics hours
            </Typography>
            <RowChevron />
          </ButtonBase>
          {rows.map((row) => {
            const rowActive = active && stateFilter === row.stateCode;
            const met = row.tracked - row.short;
            const rowLabel =
              `${row.stateCode}: ${met} of ${row.tracked} tracked license${row.tracked !== 1 ? 's have' : ' has'} met ethics hours` +
              (row.attention > 0
                ? `; ${row.attention} need${row.attention !== 1 ? '' : 's'} attention`
                : '');
            return (
              <Tooltip key={row.stateCode} title={rowLabel} arrow>
                <ButtonBase
                  onClick={() => {
                    // Compose jurisdiction + ethics-gap filters; clicking the
                    // active row unwinds both.
                    if (rowActive) {
                      onToggle(false);
                      onStateFilterChange('all');
                    } else {
                      onToggle(true);
                      onStateFilterChange(row.stateCode);
                    }
                  }}
                  aria-pressed={rowActive}
                  aria-label={rowLabel}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr 64px 16px',
                    alignItems: 'center',
                    gap: 1,
                    textAlign: 'left',
                    px: 0.75,
                    py: 0.5,
                    mx: -0.75,
                    borderRadius: '6px',
                    bgcolor: rowActive ? 'tint.neutralSoft' : 'transparent',
                    transition: 'background-color 0.15s ease-out',
                    '&:hover': { bgcolor: 'tint.neutralSoft' },
                    '&.Mui-focusVisible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '1px',
                    },
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    {row.stateCode}
                  </Typography>
                  {/* Coverage bar: fills green as ethics requirements are met;
                      the neutral remainder is what's still outstanding. */}
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: '999px',
                      bgcolor: 'tint.neutralSoft',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${(met / row.tracked) * 100}%`,
                        bgcolor: 'status.completed.dot',
                        borderRadius: 'inherit',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', textAlign: 'right', whiteSpace: 'nowrap' }}
                  >
                    {met} of {row.tracked} met
                  </Typography>
                  <RowChevron />
                </ButtonBase>
              </Tooltip>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
